import { Localized, useLocalization } from "@fluent/react"
import { unwrapResult } from "@reduxjs/toolkit"
import AddIcon from "@mui/icons-material/Add"
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline"
import EditOutlinedIcon from "@mui/icons-material/EditOutlined"
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined"
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined"
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess"
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore"
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Paper, Stack, TextField, Typography } from "@mui/material"
import { SimpleTreeView } from "@mui/x-tree-view/SimpleTreeView"
import { TreeItem } from "@mui/x-tree-view/TreeItem"
import { useEffect, useState } from "react"
import { useParams } from "react-router-dom"
import { CancelButton } from "../../app/components/CancelButton"
import { DeleteButton } from "../../app/components/DeleteButton"
import { SaveButton } from "../../app/components/SaveButton"
import { LoadingContainer } from "../../app/LoadingContainer"
import { APIError } from "../../app/appTypes"
import { useAppDispatch } from "../../app/hooks"
import {
    CreateStructureNodeRequest,
    StructureNodeTreeDto,
    StructureTreeDto,
    UpdateStructureNodeRequest,
    createStructureNode,
    deleteStructureNode,
    loadStructureTree,
    updateStructureNode,
} from "./structureSlice"
import { StructuresHeader } from "./components/StructuresHeader"

type NodeDialogMode = "add-root" | "add-child" | "edit"

interface NodeDialogState {
    open: boolean
    mode: NodeDialogMode
    parentId: string | null
    nodeId: string | null
    name: string
    code: string
    description: string
}

interface DeleteDialogState {
    open: boolean
    nodeId: string | null
    parentNodeId: string | null
    nodeLabel: string
    error: string | null
}

interface StructureNodeItemProps {
    node: StructureNodeTreeDto
    onAddChild: (parentId: string) => void
    onEdit: (node: StructureNodeTreeDto) => void
    onDelete: (node: StructureNodeTreeDto) => void
}

function findNodeById(nodes: StructureNodeTreeDto[], nodeId: string): StructureNodeTreeDto | null {
    for (const node of nodes) {
        if (node.id === nodeId) {
            return node
        }

        const childFound = findNodeById(node.children, nodeId)
        if (childFound) {
            return childFound
        }
    }

    return null
}

function collectExpandableNodeIds(nodes: StructureNodeTreeDto[]): string[] {
    return nodes.flatMap(node => {
        const nestedIds = collectExpandableNodeIds(node.children)
        return node.children.length > 0 ? [node.id, ...nestedIds] : nestedIds
    })
}

function StructureNodeItem({ node, onAddChild, onEdit, onDelete }: StructureNodeItemProps) {
    const label = `${node.code ? `${node.code} - ` : ""}${node.name}`
    const hasChildren = node.children.length > 0

    const labelNode = (
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ width: "100%", py: 0.25 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                {hasChildren ? (
                    <FolderOutlinedIcon sx={{ fontSize: 18, color: "warning.main" }} />
                ) : (
                    <SettingsOutlinedIcon sx={{ fontSize: 17, color: "action.active" }} />
                )}

                <Typography variant="body2" sx={{ pr: 1, fontSize: 13, lineHeight: 1.2 }} noWrap>
                    {label}
                </Typography>
            </Stack>

            <Stack direction="row" spacing={0.5}>
                <IconButton
                    size="small"
                    sx={{ p: 0.4 }}
                    onClick={(event) => {
                        event.stopPropagation()
                        onAddChild(node.id)
                    }}
                >
                    <AddIcon fontSize="small" />
                </IconButton>

                <IconButton
                    size="small"
                    sx={{ p: 0.4 }}
                    onClick={(event) => {
                        event.stopPropagation()
                        onEdit(node)
                    }}
                >
                    <EditOutlinedIcon fontSize="small" />
                </IconButton>

                <IconButton
                    size="small"
                    color="error"
                    sx={{ p: 0.4 }}
                    onClick={(event) => {
                        event.stopPropagation()
                        onDelete(node)
                    }}
                >
                    <DeleteOutlineIcon fontSize="small" />
                </IconButton>
            </Stack>
        </Stack>
    )

    return (
        <TreeItem itemId={node.id} label={labelNode}>
            {node.children.map(child => (
                <StructureNodeItem
                    key={child.id}
                    node={child}
                    onAddChild={onAddChild}
                    onEdit={onEdit}
                    onDelete={onDelete}
                />
            ))}
        </TreeItem>
    )
}

interface StructuresFormProps {
    structureDefinitionId?: string
    onSelectedNodeChange?: (node: StructureNodeTreeDto | null) => void
}

export const StructuresForm = ({ structureDefinitionId: structureDefinitionIdProp, onSelectedNodeChange }: StructuresFormProps) => {
    const dispatch = useAppDispatch()
    const { l10n } = useLocalization()
    const { id: paramId } = useParams()
    const structureDefinitionId = structureDefinitionIdProp ?? paramId ?? ""

    const [isLoading, setIsLoading] = useState(false)
    const [isSavingNode, setIsSavingNode] = useState(false)
    const [isDeletingNode, setIsDeletingNode] = useState(false)
    const [tree, setTree] = useState<StructureTreeDto | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [expanded, setExpanded] = useState<string[]>([])
    const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null)
    const [nodeDialog, setNodeDialog] = useState<NodeDialogState>({
        open: false,
        mode: "add-root",
        parentId: null,
        nodeId: null,
        name: "",
        code: "",
        description: "",
    })
    const [deleteDialog, setDeleteDialog] = useState<DeleteDialogState>({
        open: false,
        nodeId: null,
        parentNodeId: null,
        nodeLabel: "",
        error: null,
    })

    const reloadTree = async (focusNodeId?: string, expandNodeId?: string) => {
        const loadedTree = await dispatch(loadStructureTree({ structureDefinitionId })).then(unwrapResult)
        setTree(loadedTree)

        if (expandNodeId) {
            setExpanded(prev => Array.from(new Set([...prev, expandNodeId])))
        }

        if (focusNodeId) {
            setSelectedNodeId(focusNodeId)
        }
    }

    useEffect(() => {
        if (!structureDefinitionId) {
            setError(l10n.getString("structures-tree-missing-id", null, "Brak ID struktury w adresie URL."))
            setTree(null)
            return
        }

        setIsLoading(true)
        setError(null)

        dispatch(loadStructureTree({ structureDefinitionId }))
            .then(unwrapResult)
            .then(loadedTree => {
                setTree(loadedTree)
                onSelectedNodeChange?.(null)
            })
            .catch(() => {
                setError(l10n.getString("structures-tree-load-error", null, "Nie udało się załadować drzewa struktury."))
            })
            .finally(() => setIsLoading(false))
    }, [dispatch, structureDefinitionId, l10n, onSelectedNodeChange])

    if (isLoading) {
        return <LoadingContainer />
    }

    if (error) {
        return (
            <Paper sx={{ p: 2 }}>
                <Alert severity="error">{error}</Alert>
            </Paper>
        )
    }

    if (!tree) {
        return null
    }

    const closeDialog = () => {
        setNodeDialog(prev => ({
            ...prev,
            open: false,
        }))
    }

    const openAddRootDialog = () => {
        setNodeDialog({
            open: true,
            mode: "add-root",
            parentId: null,
            nodeId: null,
            name: "",
            code: "",
            description: "",
        })
    }

    const openAddChildDialog = (parentId: string) => {
        setNodeDialog({
            open: true,
            mode: "add-child",
            parentId,
            nodeId: null,
            name: "",
            code: "",
            description: "",
        })
    }

    const openEditDialog = (node: StructureNodeTreeDto) => {
        setNodeDialog({
            open: true,
            mode: "edit",
            parentId: node.parentNodeId,
            nodeId: node.id,
            name: node.name,
            code: node.code,
            description: node.description,
        })
    }

    const openDeleteDialog = (node: StructureNodeTreeDto) => {
        setDeleteDialog({
            open: true,
            nodeId: node.id,
            parentNodeId: node.parentNodeId,
            nodeLabel: node.code ? `${node.code} - ${node.name}` : node.name,
            error: null,
        })
    }

    const closeDeleteDialog = () => {
        if (isDeletingNode) {
            return
        }

        setDeleteDialog({
            open: false,
            nodeId: null,
            parentNodeId: null,
            nodeLabel: "",
            error: null,
        })
    }

    const deleteNode = async () => {
        if (!deleteDialog.nodeId) {
            return
        }

        setIsDeletingNode(true)
        setDeleteDialog(prev => ({ ...prev, error: null }))

        try {
            await dispatch(deleteStructureNode({ nodeId: deleteDialog.nodeId })).then(unwrapResult)
            await reloadTree(deleteDialog.parentNodeId ?? undefined, deleteDialog.parentNodeId ?? undefined)
            setDeleteDialog({
                open: false,
                nodeId: null,
                parentNodeId: null,
                nodeLabel: "",
                error: null,
            })
        } catch (e) {
            const apiError = e as APIError
            // 409: the unit (or a unit below it) still has report templates assigned.
            const conflict = apiError?.kind === "http" && apiError.status === 409 && apiError.problem
                ? l10n.getString(apiError.problem.title, null, apiError.problem.detail)
                : null

            setDeleteDialog(prev => ({
                ...prev,
                error: conflict ?? l10n.getString("structures-tree-delete-node-error", null, "Nie udało się usunąć noda."),
            }))
        } finally {
            setIsDeletingNode(false)
        }
    }

    const saveNode = async () => {
        if (!nodeDialog.name.trim()) {
            return
        }

        const parentId = nodeDialog.parentId
        const nodeId = nodeDialog.nodeId

        if (nodeDialog.mode === "add-root" || (nodeDialog.mode === "add-child" && parentId)) {
            setIsSavingNode(true)
            setError(null)

            try {
                const parentNode = parentId ? findNodeById(tree.roots, parentId) : null
                const parentChildrenCount = parentNode?.children.length ?? tree.roots.length

                const request: CreateStructureNodeRequest = {
                    structureDefinitionId: tree.structure.id,
                    parentNodeId: nodeDialog.mode === "add-child" ? parentId : null,
                    nodeTypeId: null,
                    name: nodeDialog.name.trim(),
                    code: nodeDialog.code.trim(),
                    description: nodeDialog.description.trim(),
                    order: parentChildrenCount,
                    validFrom: new Date(),
                }

                const createdNode = await dispatch(createStructureNode(request)).then(unwrapResult)
                await reloadTree(createdNode.id, nodeDialog.mode === "add-child" ? parentId ?? undefined : undefined)
                closeDialog()
                return
            } catch {
                setError(l10n.getString("structures-tree-create-node-error", null, "Nie udało się utworzyć noda."))
                return
            } finally {
                setIsSavingNode(false)
            }
        }

        if (nodeDialog.mode === "edit" && nodeId) {
            const editedNode = findNodeById(tree.roots, nodeId)
            if (!editedNode) {
                closeDialog()
                return
            }

            setIsSavingNode(true)
            setError(null)

            try {
                const request: UpdateStructureNodeRequest = {
                    id: editedNode.id,
                    parentNodeId: editedNode.parentNodeId,
                    nodeTypeId: editedNode.nodeTypeId,
                    name: nodeDialog.name.trim(),
                    code: nodeDialog.code.trim(),
                    description: nodeDialog.description.trim(),
                    order: editedNode.order,
                    validFrom: editedNode.validFrom,
                    validTo: editedNode.validTo,
                }

                const updatedNode = await dispatch(updateStructureNode(request)).then(unwrapResult)
                await reloadTree(updatedNode.id, updatedNode.parentNodeId ?? undefined)
                closeDialog()
                return
            } catch {
                setError(l10n.getString("structures-tree-update-node-error", null, "Nie udało się zaktualizować noda."))
                return
            } finally {
                setIsSavingNode(false)
            }
        }

        closeDialog()
    }

    const dialogTitleId =
        nodeDialog.mode === "add-root"
            ? "structures-tree-dialog-add-root-title"
            : nodeDialog.mode === "add-child"
                ? "structures-tree-dialog-add-child-title"
                : "structures-tree-dialog-edit-title"

    const dialogDefaultTitle =
        nodeDialog.mode === "add-root"
            ? "Dodaj node główny"
            : nodeDialog.mode === "add-child"
                ? "Dodaj node podrzędny"
                : "Edytuj node"

    return (
        <Stack spacing={2}>
            <StructuresHeader structure={tree.structure} />

            <Paper sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                    <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={1}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            <Localized id="structures-tree-title">
                                <span>_Drzewko struktury</span>
                            </Localized>
                        </Typography>

                        {tree.roots.length > 0 && (
                            <Stack direction="row" spacing={1}>
                                <Button
                                    size="small"
                                    variant="text"
                                    startIcon={<UnfoldMoreIcon />}
                                    onClick={() => setExpanded(collectExpandableNodeIds(tree.roots))}
                                >
                                    <Localized id="structure-expand-all">
                                        <span>Rozwiń wszystko</span>
                                    </Localized>
                                </Button>

                                <Button
                                    size="small"
                                    variant="text"
                                    startIcon={<UnfoldLessIcon />}
                                    onClick={() => setExpanded([])}
                                >
                                    <Localized id="structure-collapse-all">
                                        <span>Zwiń wszystko</span>
                                    </Localized>
                                </Button>
                            </Stack>
                        )}
                    </Stack>

                    {tree.roots.length === 0 ? (
                        <Box
                            sx={{
                                border: "1px dashed",
                                borderColor: "divider",
                                borderRadius: 2,
                                p: 3,
                                textAlign: "center",
                                bgcolor: "background.default",
                            }}
                        >
                            <Typography variant="h6" sx={{ mb: 1 }}>
                                <Localized id="structures-tree-empty-cta-title">
                                    <span>_Zacznij budować drzewko</span>
                                </Localized>
                            </Typography>

                            <Typography color="text.secondary" sx={{ mb: 2 }}>
                                <Localized id="structures-tree-empty-cta-description">
                                    <span>_Dodaj pierwszy node i rozbudowuj strukturę o kolejne poziomy.</span>
                                </Localized>
                            </Typography>

                            <Button variant="contained" startIcon={<AddIcon />} onClick={openAddRootDialog}>
                                <Localized id="structures-tree-add-first">
                                    <span>_Dodaj pierwszy node</span>
                                </Localized>
                            </Button>
                        </Box>
                    ) : (
                        <Box
                            sx={{
                                "& .MuiTreeItem-root": { my: 0.125 },
                                "& .MuiTreeItem-content": { py: 0.125, pr: 0.5, pl: 0.25, minHeight: 32, borderRadius: 1 },
                                "& .MuiTreeItem-groupTransition": { ml: 1.5 },
                                "& .MuiTreeItem-iconContainer": { mr: 0.5 },
                            }}
                        >
                            <SimpleTreeView
                                expandedItems={expanded}
                                onExpandedItemsChange={(_, itemIds) => setExpanded(itemIds as string[])}
                                selectedItems={selectedNodeId ?? ""}
                                onSelectedItemsChange={(_, itemId) => {
                                    const nextSelectedId = (itemId as string) ?? null
                                    setSelectedNodeId(nextSelectedId)
                                    onSelectedNodeChange?.(nextSelectedId ? findNodeById(tree.roots, nextSelectedId) : null)
                                }}
                            >
                                {tree.roots.map(root => (
                                    <StructureNodeItem
                                        key={root.id}
                                        node={root}
                                        onAddChild={openAddChildDialog}
                                        onEdit={openEditDialog}
                                        onDelete={openDeleteDialog}
                                    />
                                ))}
                            </SimpleTreeView>
                        </Box>
                    )}
                </Stack>
            </Paper>

            <Dialog open={nodeDialog.open} onClose={closeDialog} fullWidth maxWidth="sm">
                <DialogTitle sx={{ pb: 1 }}>
                    <Localized id={dialogTitleId}>
                        <span>{dialogDefaultTitle}</span>
                    </Localized>
                </DialogTitle>

                <DialogContent sx={{ pt: "12px !important" }}>
                    <Box
                        sx={{
                            display: "grid",
                            gap: 2,
                            gridTemplateColumns: { xs: "1fr", sm: "minmax(0, 1fr) minmax(0, 1fr)" },
                            alignItems: "start",
                        }}
                    >
                        <TextField
                            label={l10n.getString("structures-tree-node-code", null, "Kod")}
                            value={nodeDialog.code}
                            onChange={event => {
                                const upper = event.target.value.toUpperCase().slice(0, 50)
                                setNodeDialog(prev => ({ ...prev, code: upper }))
                            }}
                            fullWidth
                            autoFocus
                            inputProps={{ maxLength: 50 }}
                            helperText={`${nodeDialog.code.length}/50`}
                        />

                        <TextField
                            label={l10n.getString("structures-tree-node-name", null, "Nazwa")}
                            value={nodeDialog.name}
                            onChange={event => {
                                const name = event.target.value.slice(0, 200)
                                setNodeDialog(prev => ({ ...prev, name }))
                            }}
                            required
                            fullWidth
                            inputProps={{ maxLength: 200 }}
                            helperText={`${nodeDialog.name.length}/200`}
                        />

                        <TextField
                            label={l10n.getString("structures-tree-node-description", null, "Opis")}
                            value={nodeDialog.description}
                            onChange={event => {
                                const description = event.target.value.slice(0, 1000)
                                setNodeDialog(prev => ({ ...prev, description }))
                            }}
                            fullWidth
                            multiline
                            minRows={4}
                            inputProps={{ maxLength: 1000 }}
                            helperText={`${nodeDialog.description.length}/1000`}
                            sx={{ gridColumn: { xs: "1", sm: "1 / -1" } }}
                        />
                    </Box>
                </DialogContent>

                <DialogActions>
                    <SaveButton loading={isSavingNode} onClick={saveNode} disabled={!nodeDialog.name.trim() || isSavingNode} />
                    <CancelButton loading={isSavingNode} onClick={closeDialog} />
                </DialogActions>
            </Dialog>

            <Dialog open={deleteDialog.open} onClose={closeDeleteDialog} fullWidth maxWidth="xs">
                <DialogTitle sx={{ pb: 1 }}>
                    <Localized id="delete">
                        <span>Usuń node</span>
                    </Localized>
                </DialogTitle>

                <DialogContent sx={{ pt: "12px !important" }}>
                    <Stack spacing={2}>
                        <Typography variant="body2" color="text.secondary">
                            {l10n.getString("structures-tree-delete-confirm", null, "Czy na pewno chcesz usunąć ten node wraz z jego dziećmi?")}
                        </Typography>

                        {deleteDialog.nodeLabel && (
                            <Box
                                sx={{
                                    px: 1.25,
                                    py: 1,
                                    borderRadius: 1,
                                    border: "1px solid",
                                    borderColor: "divider",
                                    bgcolor: "background.default",
                                }}
                            >
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {deleteDialog.nodeLabel}
                                </Typography>
                            </Box>
                        )}

                        {deleteDialog.error && <Alert severity="error">{deleteDialog.error}</Alert>}
                    </Stack>
                </DialogContent>

                <DialogActions>
                    <DeleteButton loading={isDeletingNode} onClick={deleteNode} />
                    <CancelButton loading={isDeletingNode} onClick={closeDeleteDialog} />
                </DialogActions>
            </Dialog>
        </Stack>
    )
}
